import React, { useState, useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import Login from './components/Auth/Login';
import PhmLivePrefetch from './components/ProductiveHours/PhmLivePrefetch';
import SuiteNavbar from './components/Layout/SuiteNavbar';
import ScrollToTop from './components/Layout/ScrollToTop';
import LegacyDomainBanner from './components/Layout/LegacyDomainBanner';

import { AuthProvider, useAuth } from './context/AuthContext';
import { hasPermission } from './utils/permissions';
import { WFM_MODULES, firstOpenablePath } from './config/wfmModules';
import { canOpenAdmin } from './components/Admin/adminPages';
import { ThemeProvider } from './context/ThemeContext';
import { ToastProvider } from './context/ToastContext';
import './components/Toast/Toast.css';
import lazyModule, { preloadWhenIdle } from './utils/lazyModule';
import { PageSkeleton } from './components/common/Skeleton';
import RouteErrorBoundary from './components/common/RouteErrorBoundary';
import './components/common/AppState.css';
import './components/common/AppSelect.css';
import './App.css';
import { legacyQualityTarget } from './components/Quality/qualityViews';

// Every module is its own download (lazyModule): the first page loads only the shell and the
// module it shows, and the rest are fetched in the background once the browser is idle
// (preloadWhenIdle in AppContent), so opening one later is still instant.
const WfmHome = lazyModule(() => import('./components/WfmHome/WfmHome'));
const Dashboard = lazyModule(() => import('./components/Dashboard/Dashboard'));
const ProductiveHours = lazyModule(() => import('./components/ProductiveHours/ProductiveHours'));
const MonitorGlance = lazyModule(() => import('./components/ProductiveHours/MonitorGlance'));
const AgentDossier = lazyModule(() => import('./components/AgentDossier/AgentDossier'));
const QaDashboard = lazyModule(() => import('./components/QaDashboard/QaDashboard'));
const Schedule = lazyModule(() => import('./components/Schedule/Schedule'));
const KnowledgeHub = lazyModule(() => import('./components/KnowledgeHub/KnowledgeHub'));
const KnowledgeBase = lazyModule(() => import('./components/KnowledgeBase/KnowledgeBase'));
const TlReports = lazyModule(() => import('./components/TlReports/TlReports'));
const Statistics = lazyModule(() => import('./components/Statistics/Statistics'));
const ScorecardsHub = lazyModule(() => import('./components/Scorecards/ScorecardsHub'));
const IncentivesTargets = lazyModule(() => import('./components/Scorecards/IncentivesTargets'));
const Meetings = lazyModule(() => import('./components/Meetings/Meetings'));
const AllBreaks = lazyModule(() => import('./components/AllBreaks/AllBreaks'));
const Historic = lazyModule(() => import('./components/Historic/Historic'));
const AdminPanel = lazyModule(() => import('./components/Admin/AdminPanel'));
const AuditLog = lazyModule(() => import('./components/AuditLog/AuditLog'));
const NotificationsPage = lazyModule(() => import('./components/Notifications/NotificationsPage'));
const GlobalSearchPage = lazyModule(() => import('./components/GlobalSearch/GlobalSearchPage'));
const PhmReconcile = lazyModule(() => import('./components/ProductiveHours/PhmReconcile'));

const AuthLoadingScreen = () => <div className="container"><PageSkeleton label="Signing in" /></div>;

const LoginRoute = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, loading } = useAuth();
  const oauthCode = searchParams.get('code');
  const isOktaCallback = location.pathname === '/auth/okta/callback';
  const isSsoReturn = Boolean(oauthCode) || isOktaCallback;

  // Completing Okta sign-in must not wait on an unrelated /api/auth/me bootstrap.
  if (isSsoReturn) {
    return isAuthenticated ? <Navigate to="/home" replace /> : <Login />;
  }
  if (loading) return <AuthLoadingScreen />;
  return isAuthenticated ? <Navigate to="/home" replace /> : <Login />;
};

const HomeRedirect = () => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <AuthLoadingScreen />;
  return <Navigate to={isAuthenticated ? '/home' : '/login'} replace />;
};

const PrivateRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) return <AuthLoadingScreen />;
  
  return isAuthenticated ? children : <Navigate to="/login" />;
};

// Insights views share one page, so the date range stays put when the navbar tab changes.
// The address is the tab: /statistics (the Targets tab moved to Incentives on 2026-11-02; /statistics/targets is redirected to /incentives/targets). The list a card opens under the cards (QA reviews, weekly quizzes, bad ratings) is
// `?detail=qa|quiz|dsat`, so a link or a reload lands on it. The old pages (2026-10-28) are redirected to the card that took them over.
const STATISTICS_DETAILS = ['qa', 'quiz', 'dsat'];
function StatisticsRoute() {
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  if (/\/review\/?$/.test(pathname)) return <Navigate to="/statistics?detail=dsat" replace />;
  if (/\/quizzes\/?$/.test(pathname)) return <Navigate to="/statistics?detail=quiz" replace />;
  const view = /\/targets\/?$/.test(pathname) ? 'targets' : 'figures';
  const asked = searchParams.get('detail');
  const detail = view === 'figures' && STATISTICS_DETAILS.includes(asked) ? asked : null;
  const onDetail = (kind) => {
    const next = new URLSearchParams(searchParams);
    if (kind) next.set('detail', kind); else next.delete('detail');
    setSearchParams(next, { replace: true });
  };
  return <Statistics view={view} detail={detail} onDetail={onDetail} />;
}

// Admin access is a permission, not the `admin` slug - the suite hands admin rights out through
// Admin -> Role permissions, and the navigation already gates the tiles on admin.panel /
// admin.audit. Comparing the slug here sent a fully-granted role (Zendesk Administrator, Head of
// CS) straight back to /home from a tile it had just been shown.
const AdminRoute = ({ permission = 'admin.panel', children }) => (
  <PermissionRoute permission={permission}>{children}</PermissionRoute>
);

// The Admin Panel itself opens for anyone who can open at least one of its pages (the panel then lists
// only those), so a position given "Set Statistics targets" or "Role permissions" reaches just that page.
const AdminPanelRoute = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();
  if (loading) return <AuthLoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (!canOpenAdmin(user)) return <Navigate to="/home" />;
  return children;
};

// Shows full-screen "Server is being restarted" when API returns 503 during deploy
function MaintenanceOverlay() {
  const [show, setShow] = useState(false);
  const [message, setMessage] = useState('');
  useEffect(() => {
    const handler = (e) => {
      setMessage(e.detail?.message || 'Server is being restarted. Please wait a moment and refresh.');
      setShow(true);
    };
    window.addEventListener('server-maintenance', handler);
    return () => window.removeEventListener('server-maintenance', handler);
  }, []);
  if (!show) return null;
  return (
    <div className="maintenance-overlay" role="alert" aria-live="polite">
      <div className="maintenance-overlay-content">
        <p className="maintenance-overlay-message">{message}</p>
        <button type="button" className="btn btn-primary maintenance-overlay-btn" onClick={() => window.location.reload()}>
          Refresh
        </button>
      </div>
    </div>
  );
}

// `anyPermission`: a page two boxes open (any one of the keys is enough), like a module's `anyPermission` in wfmModules.js.
const PermissionRoute = ({ permission, anyPermission, children }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) return <AuthLoadingScreen />;

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  const allowed = Array.isArray(anyPermission) ? anyPermission.some((key) => hasPermission(user, key)) : hasPermission(user, permission);
  if (!allowed) {
    return <Navigate to="/home" />;
  }

  return children;
};

// The Monitor's Live page: all of it for Workforce Monitor access, the read-only board for Live status alone.
function MonitorLive() {
  const { user } = useAuth();
  return hasPermission(user, 'phm.access') ? <ProductiveHours /> : <MonitorGlance />;
}

const NotFoundPage = () => (
  <div
    style={{
      minHeight: '70vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.75rem',
      textAlign: 'center',
      padding: '1rem'
    }}
  >
    <h1 style={{ margin: 0 }}>404</h1>
    <h2 style={{ margin: 0, fontWeight: 500 }}>Page not found</h2>
    <p style={{ margin: 0, opacity: 0.85 }}>
      The page you are trying to open does not exist.
    </p>
    <a href="/" className="btn btn-primary" style={{ marginTop: '0.75rem' }}>
      Go to dashboard
    </a>
  </div>
);

/** The old Quality tab address: the same report, now under AI Audit in the QA tab, keeping the week and the month. */
function LegacyQualityRedirect() {
  const { search } = useLocation();
  return <Navigate to={legacyQualityTarget(search)} replace />;
}

/** The old Insights > Incentives addresses: the same page at /incentives, keeping whatever the link carries (month, position, person). */
function LegacyIncentivesRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/incentives${search}`} replace />;
}

/** /cs-reports opens the first tab this user may open; a user with neither is told so rather than shown a blank page. */
function CsReportsIndex() {
  const { user } = useAuth();
  const mod = WFM_MODULES.find((m) => m.id === 'cs-reports');
  const to = firstOpenablePath(user, mod);
  if (to !== mod.path) return <Navigate to={to} replace />;
  return <Navigate to="/home" replace />;
}

function AppContent() {
  const { showNavbar, isAuthenticated, user } = useAuth();
  const location = useLocation();
  // Moving to another module fades the new one in (App.css, .route-fade); pages inside one module
  // (the Dossier's tabs, Schedule's views) share the key, so they keep their state as before.
  const moduleKey = location.pathname.split('/')[1] || 'home';

  useEffect(() => {
    if (!isAuthenticated || !user) return undefined;
    const can = (permission) => hasPermission(user, permission);
    const manager = can('admin.panel') || can('phm.access') || can('schedule.edit');
    return preloadWhenIdle([
      WfmHome,
      can('breaks.dashboard') && Dashboard,
      can('phm.access') && ProductiveHours,
      can('dossier.access') && AgentDossier,
      (can('qa.access') || can('quality.view')) && QaDashboard,
      can('schedule.access') && Schedule,
      can('knowledge.access') && KnowledgeHub,
      can('kb.access') && KnowledgeBase,
      can('weekly_report.access') && TlReports,
      can('stats.access') && Statistics,
      (can('scorecards.view_team') || can('scorecards.view_own')) && ScorecardsHub,
      can('meetings.access') && Meetings,
      manager && AllBreaks,
      manager && Historic,
      canOpenAdmin(user) && AdminPanel,
      can('admin.audit') && AuditLog,
      can('admin.panel') && PhmReconcile
    ].filter(Boolean));
  }, [isAuthenticated, user]);

  return (
    <div className="app-container">
      {/* Keeps the PHM live cache warm on every route (self-gates on auth + phm.access), so returning
          to Productive Hours from Admin Panel / Break Scheduler shows current data immediately. */}
      <PhmLivePrefetch />
      {/* Above the navbar and outside the showNavbar gate on purpose: someone arriving on an old
          bookmark should see the new address on the login screen too. Self-gates on the hostname,
          so it renders nothing anywhere except wfm.gms.work. */}
      <LegacyDomainBanner />
      {showNavbar && <SuiteNavbar />}
      <main className={showNavbar ? 'main-content' : ''}>
        <RouteErrorBoundary resetKey={location.pathname}>
        <div key={moduleKey} className="route-fade">
        <Suspense fallback={<div className="container"><PageSkeleton label="Loading page" /></div>}>
        <Routes>
          <Route path="/login" element={<LoginRoute />} />
          <Route path="/auth/okta/callback" element={<LoginRoute />} />
          <Route path="/register" element={<Navigate to="/login" />} />
          {/* The full results of the navbar's search (Enter, or "See all results"): open to anyone signed in; each kind of result is limited to what they may open. */}
          <Route
            path="/search"
            element={
              <PrivateRoute>
                <GlobalSearchPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            }
          />
          <Route path="/my-breaks" element={<Navigate to="/dashboard" replace />} />
          <Route
            path="/all-breaks"
            element={
              <PermissionRoute permission="breaks.all_breaks">
                <AllBreaks />
              </PermissionRoute>
            }
          />
          <Route
            path="/admin/*"
            element={
              <AdminPanelRoute>
                <AdminPanel />
              </AdminPanelRoute>
            }
          />
          <Route
            path="/historic"
            element={
              <PermissionRoute permission="breaks.historic">
                <Historic />
              </PermissionRoute>
            }
          />
          <Route path="/workstreams" element={<Navigate to="/dashboard" replace />} />
          {/* Incentives: the one scorecards page (2026-10-27, Dashboards with its tabs) was a page of Insights until 2026-10-30 and is a module of its own now; every
              old address, with the month, position and person a link carries, lands on it. */}
          <Route path="/statistics/incentives" element={<LegacyIncentivesRedirect />} />
          <Route path="/statistics/scorecards" element={<LegacyIncentivesRedirect />} />
          <Route path="/statistics/dashboards" element={<LegacyIncentivesRedirect />} />
          <Route path="/statistics/my-scores" element={<LegacyIncentivesRedirect />} />
          <Route
            path="/incentives"
            element={
              <PermissionRoute anyPermission={['scorecards.view_team', 'scorecards.view_own']}>
                <ScorecardsHub />
              </PermissionRoute>
            }
          />
          <Route
            path="/incentives/targets"
            element={
              <PermissionRoute anyPermission={['scorecards.view_team', 'scorecards.view_own']}>
                <IncentivesTargets />
              </PermissionRoute>
            }
          />
          <Route path="/statistics/targets" element={<Navigate to="/incentives/targets" replace />} />
          <Route
            path="/statistics/*"
            element={
              <PermissionRoute permission="stats.access">
                <StatisticsRoute />
              </PermissionRoute>
            }
          />
          <Route
            path="/meetings/*"
            element={
              <PermissionRoute permission="meetings.access">
                <Meetings />
              </PermissionRoute>
            }
          />
          {/* CS Management Reports: the QA reports and the Knowledge & Learning report as tabs of one module. Each
              tab keeps its own permission. The old paths (saved links, bookmarks) land on the matching tab. */}
          <Route path="/cs-reports" element={<PrivateRoute><CsReportsIndex /></PrivateRoute>} />
          <Route
            path="/cs-reports/knowledge"
            element={
              <PermissionRoute permission="knowledge.access">
                <KnowledgeHub />
              </PermissionRoute>
            }
          />
          <Route
            path="/cs-reports/tl/*"
            element={
              <PermissionRoute permission="weekly_report.access">
                <TlReports />
              </PermissionRoute>
            }
          />
          {/* The Quality reports live in the QA tab, under "AI Audit" (2026-10-28); the address they briefly had still works. */}
          <Route path="/cs-reports/quality" element={<LegacyQualityRedirect />} />
          <Route path="/cs-reports/weekly" element={<Navigate to="/cs-reports/tl/weekly" replace />} />
          <Route
            path="/cs-reports/qa"
            element={
              <PermissionRoute anyPermission={['qa.access', 'quality.view']}>
                <QaDashboard />
              </PermissionRoute>
            }
          />
          <Route path="/knowledge-hub" element={<Navigate to="/cs-reports/knowledge" replace />} />
          <Route
            path="/knowledge-base"
            element={
              <PermissionRoute permission="kb.access">
                <KnowledgeBase />
              </PermissionRoute>
            }
          />
          <Route path="/qa" element={<Navigate to="/cs-reports/qa" replace />} />
          <Route path="/qa-dashboard" element={<Navigate to="/cs-reports/qa" replace />} />
          <Route path="/qa-dashboard/reports" element={<Navigate to="/cs-reports/qa" replace />} />
          <Route
            path="/notifications"
            element={
              <PrivateRoute>
                <NotificationsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/audit-log"
            element={
              <AdminRoute permission="admin.audit">
                <AuditLog />
              </AdminRoute>
            }
          />
          <Route
            path="/productive-hours"
            element={
              <PermissionRoute anyPermission={['phm.access', 'phm.view_live']}>
                <MonitorLive />
              </PermissionRoute>
            }
          />
          {/* Same component: it reads the tab off the pathname, so the module sub-nav can
              link to a tab the way every other module does. */}
          <Route
            path="/productive-hours/reports"
            element={
              <PermissionRoute permission="phm.access">
                <PermissionRoute permission="phm.view_reports">
                  <ProductiveHours />
                </PermissionRoute>
              </PermissionRoute>
            }
          />
          <Route
            path="/phm-reconcile"
            element={
              <AdminRoute>
                <PhmReconcile />
              </AdminRoute>
            }
          />
          <Route
            path="/home"
            element={
              <PrivateRoute>
                <WfmHome />
              </PrivateRoute>
            }
          />
          <Route
            path="/agent-dossier"
            element={
              <PermissionRoute permission="dossier.access">
                <AgentDossier />
              </PermissionRoute>
            }
          />
          {/* One URL per profile, so the back button goes back to the person (or the list) you came from. */}
          <Route
            path="/agent-dossier/profile/:employeeId"
            element={
              <PermissionRoute permission="dossier.access">
                <AgentDossier />
              </PermissionRoute>
            }
          />
          <Route
            path="/agent-dossier/employees"
            element={
              <PermissionRoute permission="dossier.view_employee_list">
                <AgentDossier />
              </PermissionRoute>
            }
          />
          <Route
            path="/agent-dossier/org"
            element={
              <PermissionRoute permission="dossier.view_company_chart">
                <AgentDossier />
              </PermissionRoute>
            }
          />
          <Route
            path="/agent-dossier/overview"
            element={
              <PermissionRoute permission="dossier.overview">
                <AgentDossier />
              </PermissionRoute>
            }
          />
          <Route
            path="/schedule"
            element={
              <PermissionRoute permission="schedule.access">
                <Schedule />
              </PermissionRoute>
            }
          />
          {/* On now and My shift are gone (My shift is the "My schedule" tick on the Schedule tab) and
              Leave is now a section of Summary; the old paths still land somewhere sensible. */}
          <Route path="/schedule/on-now" element={<Navigate to="/schedule" replace />} />
          <Route path="/schedule/my" element={<Navigate to="/schedule" replace />} />
          <Route path="/schedule/leave" element={<Navigate to="/schedule/summary" replace />} />
          <Route
            path="/schedule/summary"
            element={
              <PermissionRoute permission="schedule.view_summary">
                <Schedule />
              </PermissionRoute>
            }
          />
          <Route
            path="/schedule/heatmap"
            element={
              <PermissionRoute permission="schedule.view_heatmap">
                <Schedule />
              </PermissionRoute>
            }
          />
          <Route
            path="/schedule/settings"
            element={
              <PermissionRoute permission="schedule.view_settings">
                <Schedule />
              </PermissionRoute>
            }
          />
          <Route path="/" element={<HomeRedirect />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </Suspense>
        </div>
        </RouteErrorBoundary>
      </main>
    </div>
  );
}

function App() {
  return (
    <Router>
      <ScrollToTop />
      {/* AuthProvider outside ThemeProvider: the theme is saved to the account, so it reads the signed-in user. */}
      <AuthProvider>
        <ThemeProvider>
          <ToastProvider>
            <MaintenanceOverlay />
            <AppContent />
          </ToastProvider>
        </ThemeProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;

